const EMAIL_API_ENDPOINT = process.env.REACT_APP_EMAIL_API_ENDPOINT || 'https://guarding.dynamixzone.com/api/send-email.php';

export type EmailData = {
    to: string[];
    subject: string;
    body?: string;
    html?: string;
    text?: string;
    isHtml?: boolean;
    companyName?: string;
    complaintId?: string;
    surveyId?: string;
    issueType?: string;
    surveyType?: string;
};

export type EmailResult = {
    email: string;
    result?: any;
    error?: string;
};

export type EmailSendResults = {
    successful: EmailResult[];
    failed: EmailResult[];
    totalAttempted: number;
};

export type EmailValidation = {
    valid: string[];
    invalid: string[];
    isValid: boolean;
};

export const sendEmail = async (emailData: EmailData): Promise<any> => {
    try {
        const response = await fetch(EMAIL_API_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(emailData)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error sending email:', error);
        throw error;
    }
};

export const sendEmailsWithErrorHandling = async (emailData: EmailData): Promise<EmailSendResults> => {
    const results: EmailSendResults = {
        successful: [],
        failed: [],
        totalAttempted: emailData.to.length
    };

    for (const recipient of emailData.to) {
        try {
            const singleEmailData = {
                to: [recipient],
                subject: emailData.subject,
                html: emailData.body || emailData.html,
                text: emailData.text || (emailData.body && !emailData.isHtml ? emailData.body : null),
                companyName: emailData.companyName || ''
            };

            const response = await fetch(EMAIL_API_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(singleEmailData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const result = await response.json();
            results.successful.push({
                email: recipient,
                result: result
            });
            console.log(`✅ Email sent successfully to: ${recipient}`);
        } catch (error: any) {
            console.error(`❌ Failed to send email to ${recipient}:`, error);
            results.failed.push({
                email: recipient,
                error: error.message
            });
        }
    }

    return results;
};

export const fetchPSOEmail = async (db: any, bankId: string, uid: string): Promise<string | null> => {
    try {
        const { doc, getDoc } = await import('firebase/firestore');
        const psoDoc = await getDoc(doc(db, 'banks', bankId, 'pos', uid));

        if (psoDoc.exists()) {
            const psoData = psoDoc.data();
            const email = psoData.email;

            if (email) {
                const name = psoData.name || uid;
                console.log(`📧 PSO Email found: ${email} (Created by ${name})`);
                return email;
            } else {
                console.log(`❌ PSO email field is empty for UID: ${uid}`);
                return null;
            }
        } else {
            console.log(`❌ PSO document not found for UID: ${uid} in bank: ${bankId}`);
            return null;
        }
    } catch (error) {
        console.error('Error fetching PSO email:', error);
        return null;
    }
};

export const validateEmails = (emailString: string): EmailValidation => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = emailString
        .split(/[,;\n]/)
        .map(email => email.trim())
        .filter(email => email.length > 0);

    const validEmails = emails.filter(email => emailRegex.test(email));
    const invalidEmails = emails.filter(email => !emailRegex.test(email));

    return {
        valid: validEmails,
        invalid: invalidEmails,
        isValid: invalidEmails.length === 0 && validEmails.length > 0
    };
};

type Complaint = {
    issueType?: string;
    surveyData?: {
        surveyId?: string;
        branch?: {
            branchCode?: string;
            address?: string;
        };
        dateOfVisit?: string;
    };
    status?: string;
    issues?: string[];
    customIssues?: Array<{
        heading?: string;
        description?: string;
    }>;
};

type Survey = {
    surveyId?: string;
    dateOfVisit?: string;
    branch?: {
        branchCode?: string;
        address?: string;
    };
    jointVisit?: string;
    jointTeams?: string[];
    cctv?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    bams?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    guarding?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    customIssues?: Array<{
        heading?: string;
        description?: string;
    }>;
};

type CompanyDetails = {
    companyName?: string;
    imgUrl?: string;
};

export const formatEmailBody = (complaint: Complaint): string => {
    const issues = complaint.issues && complaint.issues.length > 0
        ? complaint.issues.join(', ')
        : complaint.customIssues && complaint.customIssues.length > 0
            ? complaint.customIssues.map(issue => `${issue.heading}: ${issue.description}`).join('\n')
            : 'No specific issues listed';

    return `Dear Sir/Madam,

This is regarding the ${complaint.issueType?.toUpperCase()} complaint for Survey ID: ${complaint.surveyData?.surveyId}.

DETAILS:
- Branch: ${complaint.surveyData?.branch?.branchCode}
- Address: ${complaint.surveyData?.branch?.address}
- Date of Visit: ${complaint.surveyData?.dateOfVisit}
- Current Status: ${complaint.status?.toUpperCase()}

ISSUES IDENTIFIED:
${issues}

Please review and take necessary action to resolve these issues.

Best regards,
PSO Team`;
};

export const formatSurveyEmailBody = (survey: Survey, companyDetails: CompanyDetails, psoName: string): string => {
    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    const formatIssues = (issues?: string[], otherIssues?: string[]) => {
        const allIssues: string[] = [];
        if (issues && issues.length > 0) {
            allIssues.push(...issues);
        }
        if (otherIssues && otherIssues.length > 0) {
            allIssues.push(...otherIssues);
        }
        return allIssues.length > 0 ? allIssues.join(', ') : 'No issues';
    };

    const htmlBody = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Survey Report</title>
        <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; }
            .header { background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); color: #333; padding: 20px; border-radius: 8px 8px 0 0; border: 2px solid #dee2e6; }
            .logo { max-height: 50px; max-width: 150px; }
            .content { background: #ffffff; padding: 20px; border-radius: 0 0 8px 8px; border: 2px solid #dee2e6; border-top: none; }
            .survey-details { background: #f8f9fa; padding: 15px; border-radius: 5px; margin: 15px 0; border-left: 4px solid #007bff; }
            .status-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            .status-table th, .status-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .status-table th { background-color: #007bff; color: white; }
            .status-good { color: #28a745; font-weight: bold; }
            .status-issue { color: #dc3545; font-weight: bold; }
            .joint-teams { display: inline-block; background: #007bff; color: white; padding: 4px 8px; border-radius: 12px; margin: 2px; font-size: 12px; }
            .footer { margin-top: 20px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; }
        </style>
    </head>
    <body>
        <div class="header">
            ${companyDetails?.imgUrl ? `<img src="${companyDetails.imgUrl}" alt="Company Logo" class="logo">` : ''}
            <h2>${companyDetails?.companyName || 'Company Name'}</h2>
            <p>Date: ${currentDate}</p>
        </div>
        
        <div class="content">
            <h3>Subject: SURVEY REPORT</h3>
            
            <div class="survey-details">
                <p><strong>Survey ID:</strong> ${survey.surveyId || 'N/A'}</p>
                <p><strong>PSO:</strong> ${psoName}</p>
                <p><strong>Branch:</strong> ${survey.branch?.branchCode || 'N/A'} (${survey.branch?.address || 'N/A'})</p>
                <p><strong>Date of Visit:</strong> ${survey.dateOfVisit || 'N/A'}</p>
                <p><strong>Joint Visit:</strong> ${survey.jointVisit || 'N/A'}</p>
                ${survey.jointVisit === 'Yes' && survey.jointTeams && survey.jointTeams.length > 0
            ? `<p><strong>Joint Teams:</strong> ${survey.jointTeams.map(team => `<span class="joint-teams">${team}</span>`).join(' ')}</p>`
            : ''}
            </div>
            
            <table class="status-table">
                <thead>
                    <tr>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Issues</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>CCTV</strong></td>
                        <td class="${survey.cctv?.status === 'Good' ? 'status-good' : 'status-issue'}">${survey.cctv?.status || 'N/A'}</td>
                        <td>${formatIssues(survey.cctv?.issues, survey.cctv?.otherIssues)}</td>
                    </tr>
                    <tr>
                        <td><strong>BAMS</strong></td>
                        <td class="${survey.bams?.status === 'Good' ? 'status-good' : 'status-issue'}">${survey.bams?.status || 'N/A'}</td>
                        <td>${formatIssues(survey.bams?.issues, survey.bams?.otherIssues)}</td>
                    </tr>
                    <tr>
                        <td><strong>Guarding</strong></td>
                        <td class="${survey.guarding?.status === 'Good' ? 'status-good' : 'status-issue'}">${survey.guarding?.status || 'N/A'}</td>
                        <td>${formatIssues(survey.guarding?.issues, survey.guarding?.otherIssues)}</td>
                    </tr>
                </tbody>
            </table>
            
            ${survey.customIssues && survey.customIssues.length > 0 ? `
            <div class="survey-details">
                <h4>Additional Issues:</h4>
                ${survey.customIssues.map(issue => `
                    <div style="margin: 10px 0; padding: 10px; background: #f8f9fa; border-radius: 5px;">
                        <strong style="color: #007bff;">${issue.heading || 'Custom Issue'}</strong><br>
                        ${issue.description || 'No description'}
                    </div>
                `).join('')}
            </div>
            ` : ''}
            
            <div class="footer">
                <p><strong>Signature:</strong> _________________</p>
                <p><strong>Name:</strong> _________________</p>
                <p><strong>Designation:</strong> _________________</p>
            </div>
        </div>
    </body>
    </html>
    `;

    return htmlBody;
};

export const formatComplaintEmailBody = (complaint: Complaint, companyDetails: CompanyDetails, psoName: string): string => {
    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    const htmlBody = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Complaint Report</title>
        <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; }
            .header { background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); color: #333; padding: 20px; border-radius: 8px 8px 0 0; border: 2px solid #dee2e6; }
            .logo { max-height: 50px; max-width: 150px; }
            .content { background: #ffffff; padding: 20px; border-radius: 0 0 8px 8px; border: 2px solid #dee2e6; border-top: none; }
            .complaint-details { background: #f8f9fa; padding: 15px; border-radius: 5px; margin: 15px 0; border-left: 4px solid #dc3545; }
            .issues-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            .issues-table th, .issues-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .issues-table th { background-color: #dc3545; color: white; }
            .status-badge { display: inline-block; padding: 4px 8px; border-radius: 12px; font-size: 12px; font-weight: bold; }
            .status-pending { background: #ffc107; color: #000; }
            .status-resolved { background: #28a745; color: white; }
            .status-in-progress { background: #17a2b8; color: white; }
            .status-closed { background: #6c757d; color: white; }
            .footer { margin-top: 20px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; }
        </style>
    </head>
    <body>
        <div class="header">
            ${companyDetails?.imgUrl ? `<img src="${companyDetails.imgUrl}" alt="Company Logo" class="logo">` : ''}
            <h2>${companyDetails?.companyName || 'Company Name'}</h2>
            <p>Date: ${currentDate}</p>
        </div>
        
        <div class="content">
            <h3>To: ${(complaint as any).targetCompanyName || companyDetails?.companyName || 'Company Name'}</h3>
            <h3>Subject: ${complaint.issueType?.toUpperCase()} COMPLAINT</h3>
            
            <div class="complaint-details">
                <p><strong>Survey ID:</strong> ${complaint.surveyData?.surveyId || 'N/A'}</p>
                <p><strong>Branch:</strong> ${complaint.surveyData?.branch?.branchCode || 'N/A'} (${complaint.surveyData?.branch?.address || 'N/A'})</p>
                <p><strong>Date of Visit:</strong> ${complaint.surveyData?.dateOfVisit || 'N/A'}</p>
                <p><strong>Status:</strong> <span class="status-badge status-${complaint.status || 'pending'}">${(complaint.status || 'pending').toUpperCase()}</span></p>
                <p><strong>PSO:</strong> ${psoName}</p>
            </div>
            
            <h4>Issues Identified:</h4>
            ${complaint.issues && complaint.issues.length > 0 ? `
                <table class="issues-table">
                    <thead>
                        <tr>
                            <th>Sr. No.</th>
                            <th>Issue</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${complaint.issues.map((issue, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${issue}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            ` : complaint.customIssues && complaint.customIssues.length > 0 ? `
                <table class="issues-table">
                    <thead>
                        <tr>
                            <th>Sr. No.</th>
                            <th>Issue</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${complaint.customIssues.map((issue, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td><strong>${issue.heading || 'Custom Issue'}:</strong> ${issue.description || 'No description'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            ` : '<p>No specific issues listed</p>'}
            
            <div class="footer">
                <p>Please review and take necessary action to resolve these issues.</p>
                <p><strong>Best regards,</strong><br>PSO Team</p>
            </div>
        </div>
    </body>
    </html>
    `;

    return htmlBody;
};
